/**
 * Manual Service - Read-Only
 *
 * PURPOSE: Fetch manual sections and articles from database
 *
 * RESTRICTIONS:
 * - SELECT operations only
 * - No INSERT, UPDATE, DELETE operations
 * - No RPC function calls
 * - Published content only (is_published = true)
 */

import { supabase } from '../lib/supabase';
import type { ManualSection, ManualArticle } from '../lib/supabase.types';

/**
 * Fetch all published sections ordered by order_index
 */
export async function getPublishedSections(language: string = 'ja'): Promise<ManualSection[]> {
  const { data, error } = await supabase
    .from('manual_sections')
    .select('*')
    .eq('is_published', true)
    .eq('language', language)
    .order('order_index', { ascending: true });

  if (error) {
    console.error('Error fetching sections:', error);
    throw error;
  }

  return data || [];
}

/**
 * Fetch all published articles for a specific section
 * Ordered by order_index
 */
export async function getPublishedArticlesBySection(
  sectionId: string,
  language: string = 'ja'
): Promise<ManualArticle[]> {
  const { data, error } = await supabase
    .from('manual_articles')
    .select('*')
    .eq('section_id', sectionId)
    .eq('is_published', true)
    .eq('language', language)
    .order('order_index', { ascending: true });

  if (error) {
    console.error('Error fetching articles:', error);
    throw error;
  }

  return data || [];
}

/**
 * Fetch a single article by slug
 */
export async function getArticleBySlug(slug: string, language: string = 'ja'): Promise<ManualArticle | null> {
  const { data, error } = await supabase
    .from('manual_articles')
    .select('*')
    .eq('slug', slug)
    .eq('is_published', true)
    .eq('language', language)
    .maybeSingle();

  if (error) {
    console.error('Error fetching article:', error);
    throw error;
  }

  return data;
}

/**
 * Search articles by query string
 * Searches in title, description, and tags
 * Client-side filtering for simplicity
 */
export async function searchArticles(query: string, language: string = 'ja'): Promise<ManualArticle[]> {
  // Fetch all published articles
  const { data, error } = await supabase
    .from('manual_articles')
    .select('*')
    .eq('is_published', true)
    .eq('language', language)
    .order('order_index', { ascending: true });

  if (error) {
    console.error('Error searching articles:', error);
    throw error;
  }

  if (!data) {
    return [];
  }

  // Filter on client side
  const lowerQuery = query.toLowerCase();
  return data.filter(article => {
    const titleMatch = article.title.toLowerCase().includes(lowerQuery);
    const descMatch = article.description?.toLowerCase().includes(lowerQuery) || false;
    const tagsMatch = article.tags?.some(tag => tag.toLowerCase().includes(lowerQuery)) || false;

    return titleMatch || descMatch || tagsMatch;
  });
}

/**
 * Fetch related articles by slugs
 */
export async function getRelatedArticles(slugs: string[], language: string = 'ja'): Promise<ManualArticle[]> {
  if (!slugs || slugs.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('manual_articles')
    .select('*')
    .in('slug', slugs)
    .eq('is_published', true)
    .eq('language', language);

  if (error) {
    console.error('Error fetching related articles:', error);
    throw error;
  }

  return data || [];
}

/**
 * Fetch section with its articles
 */
export async function getSectionWithArticles(sectionId: string, language: string = 'ja'): Promise<{
  section: ManualSection | null;
  articles: ManualArticle[];
}> {
  const { data: section, error: sectionError } = await supabase
    .from('manual_sections')
    .select('*')
    .eq('id', sectionId)
    .eq('is_published', true)
    .eq('language', language)
    .maybeSingle();

  if (sectionError) {
    console.error('Error fetching section:', sectionError);
    throw sectionError;
  }

  if (!section) {
    return { section: null, articles: [] };
  }

  const articles = await getPublishedArticlesBySection(sectionId, language);

  return { section, articles };
}
